"use client";

import { useCallback } from "react";
import { Info } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useAppDispatch } from "@/app/lib/features/redux/redux.hook";
import useProductReview from "@/app/lib/features/productReview/productReview.hook";
import { 
  setSellerFilter as setSellerFilterAction, 
  setAppliedFilters 
} from "@/app/lib/features/productReview/productReview.slice";
import { getProductReviewsList as getProductReviewsListThunk } from "@/app/lib/features/productReview/productReview.thunk";
import { ReviewSellerData } from "@/app/lib/features/productReview/productReview.types";
import { trackInteraction } from "@/app/lib/tracking/interactions";
import { Label } from "@/app/components/shadcn/label";
import { Checkbox } from "@/app/components/shadcn/checkbox";
import { Badge } from "@/app/components/shadcn/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/app/components/shadcn/tooltip";
import FallbackImage from "@/app/components/elements/FallbackImage";

interface ReviewSellerInfoProps {
  data: ReviewSellerData;
}

const ReviewSellerInfo = ({ data }: ReviewSellerInfoProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { appliedFilters, pendingFilters, currentProductSlug } = useProductReview();

  const isChecked = appliedFilters.seller_id === data.id;

  const handleCheckboxChange = useCallback(
    (checked: boolean | "indeterminate") => {
      if (!currentProductSlug) return;

      const newFilters = {
        ...pendingFilters,
        seller_id: checked === true ? data.id : undefined,
      };

      dispatch(setSellerFilterAction(checked === true ? data.id : undefined));

      // Satıcıya göre filtreleme = satıcı ile etkileşim (yalnızca işaretlenince).
      if (checked === true) trackInteraction("seller", data.id, "click");

      setTimeout(() => {
        const params = new URLSearchParams();

        if (newFilters.rating && newFilters.rating.length > 0) {
          newFilters.rating.forEach((r) => params.append("rating", r.toString()));
        }

        if (newFilters.topic && newFilters.topic.length > 0) {
          newFilters.topic.forEach((t) => params.append("topic", t));
        }

        if (newFilters.seller_id) {
          params.set("seller_id", newFilters.seller_id.toString());
        }

        if (newFilters.search) {
          params.set("search", newFilters.search);
        }

        if (newFilters.sort) {
          params.set("sort", newFilters.sort);
        }

        const queryString = params.toString();
        const newUrl = queryString ? `?${queryString}` : window.location.pathname;
        
        // Use router.replace with scroll: false to prevent scrolling to top
        router.replace(newUrl, { scroll: false });

        dispatch(setAppliedFilters(newFilters));

        dispatch(
          getProductReviewsListThunk({
            productSlug: currentProductSlug,
            filters: { ...newFilters, page: 1, per_page: 15 },
          }),
        );
      }, 100);
    },
    [data.id, dispatch, router, currentProductSlug, pendingFilters],
  );

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-3">
        {data.logo && (
          <div className="relative w-10 h-10 rounded-lg overflow-hidden bg-gray-100">
            <FallbackImage
              src={data.logo}
              alt={data.name}
              fill
              className="object-cover"
              fallbackClassName="object-contain p-2 opacity-60"
            />
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-gray-900">{data.name}</h4>
            <Badge className="bg-green-600 text-white rounded-md text-xs px-1">
              {data.rating}
            </Badge>
            <Tooltip>
              <TooltipTrigger>
                <Info className="w-4 h-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent variant="white" className="max-w-sm p-4">
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">{data.rating_text}</h4>
                      <Badge className="bg-green-600">{data.rating}</Badge>
                    </div>
                    <p className="text-xs text-gray-600">
                      {data.rating_description}
                    </p>
                  </div>
                  {data.seller_types && data.seller_types.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3">
                        {data.seller_types_title}
                      </h4>
                      <div className="space-y-3">
                        {data.seller_types.map((type, index) => (
                          <div key={index} className="flex gap-3">
                            <div className="w-5 h-5 rounded flex items-center justify-center shrink-0">
                              <Image
                                src={type.icon}
                                alt={type.title}
                                width={20}
                                height={20}
                              />
                            </div>
                            <div>
                              <h5 className="font-medium text-sm">
                                {type.title}
                              </h5>
                              <p className="text-xs text-gray-600">
                                {type.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>

      <hr className="my-4 border-gray-300" />

      {data.info_buttons && data.info_buttons.length > 0 && (
        <div className="space-y-2">
          {data.info_buttons.map((button, index) => (
            <Label
              key={index}
              className="flex items-center gap-2 text-sm cursor-pointer"
            >
              <Checkbox
                checked={isChecked}
                onCheckedChange={handleCheckboxChange}
              />
              {button.text}
            </Label>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewSellerInfo;